import {
  Banknote,
  CreditCard,
  ExternalLink,
  Loader2,
  Printer,
  QrCode,
  X,
} from 'lucide-react'
import qrcode from 'qrcode'
import { ReactNode, useEffect, useState } from 'react'

import { Money, StatusBadge } from '@/components/ds'
import {
  CircleCheckIcon,
  type CircleCheckIconHandle,
} from '@/components/ui/animated-icons/circle-check'
import { usePlayOnMount } from '@/components/ui/animated-icons/use-play-on-mount'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Item, Sale } from '@/interfaces/cash-register/Item'
import { PaymentType } from '@invo.space/shared'
import { cn } from '@/lib/utils'

import type { SuccessCreateReceiptResponse } from './createReceipt'
import { ReceiptLines } from './ItemList'
import { PaymentConfirm } from './PaymentConfirm'
import {
  formatQty,
  paymentTypeLabel,
  plural,
  ticketTotals,
} from './registerUtils'

export interface PaymentTypeConfig {
  type: PaymentType
  enabled: boolean
  allowed: boolean
  disallowedTooltip?: string
}

const paymentIcon: Record<PaymentType, ReactNode> = {
  [PaymentType.Cash]: <Banknote className="size-5" aria-hidden />,
  [PaymentType.Card]: <CreditCard className="size-5" aria-hidden />,
  [PaymentType.Qr]: <QrCode className="size-5" aria-hidden />,
}

interface TicketProps {
  items: Item[]
  isVatPayer: boolean
  paymentTypes: PaymentTypeConfig[]
  onRemove: (index: number) => void
  onQuantityChange: (index: number, quantity: number) => void
  onSaleChange: (index: number, sale: Sale) => void
  onDescriptionChange: (index: number, description: string | undefined) => void
  onClear: () => void
  /** Vymazání tlačítkem Vymazat, s možností vrátit (jinak se použije onClear) */
  onDiscard?: () => void
  onComplete: (
    paymentType: PaymentType,
  ) => Promise<SuccessCreateReceiptResponse>
  /** Změní způsob platby uložené účtenky, dokud nezačne nová */
  onChangePayment: (paymentType: PaymentType) => Promise<SuccessCreateReceiptResponse>
  onPrint: () => void
}

const paidWithLabel: Record<PaymentType, string> = {
  [PaymentType.Cash]: 'hotově',
  [PaymentType.Card]: 'kartou',
  [PaymentType.Qr]: 'QR kódem',
}

type Paid = {
  paymentType: PaymentType
  total: number
  permalink: string
  qr?: string
}

/** Pravý panel pokladny jako jedna jemná plocha bez rámečku. Jen dvě odsazené linky: pod záhlavím a nad souhrnem. */
export function Ticket({
  items,
  isVatPayer,
  paymentTypes,
  onRemove,
  onQuantityChange,
  onSaleChange,
  onDescriptionChange,
  onClear,
  onDiscard,
  onComplete,
  onChangePayment,
  onPrint,
}: TicketProps) {
  const [paying, setPaying] = useState<PaymentType | null>(null)
  // hotově a kartou se nejdřív potvrzuje, do té doby jde způsob platby změnit
  const [confirming, setConfirming] = useState<PaymentType | null>(null)
  const [paid, setPaid] = useState<Paid | null>(null)
  const [error, setError] = useState('')
  const [qrOpen, setQrOpen] = useState(false)
  const [qrImage, setQrImage] = useState<string | null>(null)

  const totals = ticketTotals(items)
  const hasItems = items.length > 0
  const pieces = items.reduce((sum, item) => sum + item.amount, 0)

  useEffect(() => {
    if (!paid?.qr) {
      setQrImage(null)
      return
    }
    qrcode
      .toDataURL(paid.qr, {
        width: 512,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' },
      })
      .then(setQrImage)
      .catch(console.error)
  }, [paid?.qr])

  const handleComplete = (paymentType: PaymentType) => {
    if (paying) return
    setError('')
    setPaying(paymentType)
    const total = totals.total
    onComplete(paymentType)
      .then((response) => {
        setConfirming(null)
        setPaid({
          paymentType,
          total,
          permalink: response.permalink,
          qr: response.qr,
        })
        if (paymentType === PaymentType.Qr && response.qr) setQrOpen(true)
      })
      .catch(() => setError('Nepodařilo se uložit platbu. Zkuste to znovu.'))
      .finally(() => setPaying(null))
  }

  const handleChangePayment = (paymentType: PaymentType) => {
    if (paying || !paid) return
    setError('')
    setPaying(paymentType)
    onChangePayment(paymentType)
      .then((response) => {
        setPaid({ ...paid, paymentType, permalink: response.permalink, qr: response.qr })
        if (paymentType === PaymentType.Qr && response.qr) setQrOpen(true)
      })
      .catch(() => setError('Nepodařilo se změnit způsob platby.'))
      .finally(() => setPaying(null))
  }

  const reset = () => {
    setPaid(null)
    setQrOpen(false)
    onClear()
  }

  const buttons = paymentTypes.filter((p) => p.enabled)

  return (
    <section
      aria-label="Účtenka"
      className="@container flex min-h-[420px] flex-col overflow-hidden rounded-xl bg-muted/70 @[52rem]:h-full @[52rem]:min-h-0"
    >
      <header className="mx-5 flex h-16 shrink-0 items-center justify-between gap-3 border-b pt-1">
        <div className="flex min-w-0 items-baseline gap-2">
          <h2 className="text-base font-semibold tracking-[-0.01em]">
            Účtenka
          </h2>
          {paid ? (
            <StatusBadge status="paid" label="Zaplaceno" />
          ) : (
            hasItems && (
              <span className="text-xs text-muted-foreground tabular">
                {formatQty(pieces)} {plural(pieces, 'kus', 'kusy', 'kusů')}
              </span>
            )
          )}
        </div>
        {hasItems && !paid && (
          <Button
            variant="ghost"
            className="-mr-3 h-11 px-3 hover:bg-background"
            onClick={onDiscard ?? onClear}
            disabled={!!paying}
          >
            Vymazat
          </Button>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
        {hasItems ? (
          <ReceiptLines
            items={items}
            readOnly={!!paid || !!paying}
            onRemoveClick={onRemove}
            onQuantityChange={onQuantityChange}
            onSaleChange={onSaleChange}
            onDescriptionChange={onDescriptionChange}
            isVatPayer={isVatPayer}
          />
        ) : (
          <div className="grid h-full min-h-40 place-content-center px-8 text-center">
            <p className="text-sm font-medium">Účtenka je prázdná</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Přidejte produkt z katalogu nebo vlastní položku.
            </p>
          </div>
        )}
      </div>

      <div className="mx-5 shrink-0 border-t">
        <dl className="space-y-1.5 pt-4 text-sm">
          {(totals.discount > 0 || totals.taxes.length > 0) && (
            <div className="flex items-baseline justify-between text-muted-foreground">
              <dt>Mezisoučet</dt>
              <dd>
                <Money
                  amount={totals.subtotal}
                  minor={false}
                  size="sm"
                  className="font-normal"
                  animate
                />
              </dd>
            </div>
          )}
          {totals.discount > 0 && (
            <div className="flex items-baseline justify-between text-muted-foreground">
              <dt>Sleva</dt>
              <dd>
                <Money
                  amount={-totals.discount}
                  minor={false}
                  size="sm"
                  className="font-normal"
                />
              </dd>
            </div>
          )}
          {isVatPayer &&
            totals.taxes.map((t) => (
              <div
                key={t.rate}
                className="flex items-baseline justify-between text-muted-foreground"
              >
                <dt>z toho DPH {t.rate} %</dt>
                <dd>
                  <Money
                    amount={t.tax}
                    minor={false}
                    size="sm"
                    className="font-normal"
                    animate
                  />
                </dd>
              </div>
            ))}
          <div className="flex items-baseline justify-between gap-4 pt-2">
            <dt className="text-sm font-medium">
              {isVatPayer ? 'Celkem s DPH' : 'Celkem'}
            </dt>
            <dd>
              <Money
                amount={paid ? paid.total : totals.total}
                minor={false}
                size="xl"
                animate
              />
            </dd>
          </div>
        </dl>

        {error && (
          <div
            role="alert"
            className="mt-3 flex items-center justify-between gap-2 rounded-md bg-danger-bg py-1 pr-1 pl-3 text-sm text-danger"
          >
            {error}
            <button
              type="button"
              onClick={() => setError('')}
              aria-label="Zavřít"
              className="inline-flex size-9 items-center justify-center rounded-md hover:bg-background/60"
            >
              <X className="size-4" />
            </button>
          </div>
        )}

        {paid ? (
          <div className="space-y-2 pt-4 pb-5">
            <p className="flex items-center gap-2 text-sm text-success">
              <PaidCheck />
              Zaplaceno {paidWithLabel[paid.paymentType]}, účtenka je uložená.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                className="h-14 text-base"
                onClick={() => {
                  onPrint()
                  reset()
                }}
              >
                <Printer className="size-5" />
                Vytisknout
              </Button>
              <Button className="h-14 text-base" onClick={reset}>
                Nová účtenka
              </Button>
            </div>
            {buttons.some((p) => p.type !== paid.paymentType) && (
              <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
                Zákazník chce platit jinak:
                {buttons
                  .filter((p) => p.type !== paid.paymentType)
                  .map(({ type, allowed }) => (
                    <Button
                      key={type}
                      variant="link"
                      className="h-11 text-sm"
                      disabled={!allowed || !!paying}
                      onClick={() => handleChangePayment(type)}
                    >
                      {paying === type ? <Loader2 className="animate-spin" /> : paymentIcon[type]}
                      {paymentTypeLabel[type]}
                    </Button>
                  ))}
              </div>
            )}
            <div className="flex flex-wrap gap-x-4">
              {paid.qr && (
                <Button
                  variant="link"
                  className="h-11 text-sm"
                  onClick={() => setQrOpen(true)}
                >
                  <QrCode />
                  Znovu ukázat QR kód
                </Button>
              )}
              <Button variant="link" className="h-11 text-sm" asChild>
                <a href={paid.permalink} target="_blank" rel="noreferrer">
                  Otevřít účtenku
                  <ExternalLink />
                </a>
              </Button>
            </div>
          </div>
        ) : (
          <div className="pt-4 pb-5">
            {confirming ? (
              <PaymentConfirm
                type={confirming}
                total={totals.total}
                busy={paying === confirming}
                onConfirm={() => handleComplete(confirming)}
                onBack={() => setConfirming(null)}
              />
            ) : (
              <div
                role="group"
                aria-label="Přijmout platbu"
                className={cn(
                  'grid gap-2',
                  buttons.length === 1
                    ? 'grid-cols-1'
                    : buttons.length === 2
                      ? 'grid-cols-2'
                      : 'grid-cols-3',
                )}
              >
                {buttons.map(({ type, allowed, disallowedTooltip }) => {
                  const button = (
                    <Button
                      key={type}
                      className="h-14 w-full gap-2 px-2 text-base"
                      disabled={!hasItems || !allowed || !!paying}
                      onClick={() =>
                        type === PaymentType.Qr
                          ? handleComplete(type)
                          : setConfirming(type)
                      }
                    >
                      {paying === type ? (
                        <Loader2 className="size-5 animate-spin" aria-hidden />
                      ) : (
                        paymentIcon[type]
                      )}
                      {paymentTypeLabel[type]}
                    </Button>
                  )

                  if (!allowed && disallowedTooltip) {
                    return (
                      <Tooltip key={type}>
                        <TooltipTrigger asChild>
                          <div className="cursor-not-allowed">{button}</div>
                        </TooltipTrigger>
                        <TooltipContent>{disallowedTooltip}</TooltipContent>
                      </Tooltip>
                    )
                  }
                  return button
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="flex max-h-[95vh] flex-col items-center sm:max-w-xl">
          <DialogHeader className="items-center text-center">
            <DialogTitle>Platba QR kódem</DialogTitle>
            <DialogDescription>
              Zákazník naskenuje kód v aplikaci své banky.
            </DialogDescription>
          </DialogHeader>
          {qrImage && (
            <img
              src={qrImage}
              alt="QR kód pro platbu"
              className="aspect-square w-full max-w-[420px] rounded-lg"
            />
          )}
          {paid && <Money amount={paid.total} minor={false} size="xl" />}
          <DialogFooter className="w-full sm:justify-center">
            <Button
              className="h-12 w-full sm:w-48"
              onClick={() => setQrOpen(false)}
            >
              Hotovo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}

/** Fajfka se po zaplacení jednou dokreslí. */
const PaidCheck = () => {
  const ref = usePlayOnMount<CircleCheckIconHandle>()
  return <CircleCheckIcon ref={ref} size={16} aria-hidden />
}
